import React, { useState, useEffect } from "react";

// --- Core Imports ---
import { useAuth } from "../../contexts/AuthContext"; // Import the useAuth hook

// --- Component Imports ---
import PersonalDetails from "./PersonalDetails";
import QualificationDetails from "./QualificationDetails";
import SocialMediaLinks from "./SocialMediaLink";
import CertificationHistory from "./CertificationHistory";
import axiosInstance from "../../axiosInstance";

// --- Asset Imports ---
import profileicon from "../../Images/user-1.jpg";
import coverphoto from "../../Images/Designer.png";

// --- Styles ---
import "./AccountSettings.scss";

function AccountSettings() {
  // Get user data and loading state directly from AuthContext
  const { user, isLoading } = useAuth();

  const [activeTab, setActiveTab] = useState("ProfileDetails");
  const [profileData, setProfileData] = useState(null);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    // Fetch detailed profile data only when the user object is available
    if (user) {
      setIsFetching(true);
      axiosInstance
        .get(
          `${process.env.REACT_APP_API_EMPLOYEE_URL}/employee/profile-Details`
        )
        .then((res) => {
          if (res.status === 200) {
            setProfileData(res.data.data);
          }
        })
        .catch((err) => console.error("Error fetching profile details:", err))
        .finally(() => setIsFetching(false));
    } else if (!isLoading) {
      // If auth is not loading and user is still null, stop fetching
      setIsFetching(false);
    }
  }, [user, isLoading]); // Re-run effect if the user object changes

  // Show a loading state while fetching context or details
  if (isLoading || isFetching) {
    return (
      <div className="account-settings-loading">
        <div className="loading-spinner"></div>
        <p>Loading settings...</p>
      </div>
    );
  }

  // Handle case where user is not logged in
  if (!user) {
    return (
      <div className="account-settings-loading">
        <p>Could not load user settings. Please try logging in again.</p>
      </div>
    );
  }

  // Derive display data from the user object in the context
  const { employeeName, role, profileImage } = user;
  const imageSrc = profileImage
    ? `data:image/png;base64,${profileImage}`
    : profileicon;
  const displayRole = role ? role.replace("ROLE_", "") : "No Role Assigned";

  return (
    <div className="account-settings">
      <div className="cover-photo">
        <img src={coverphoto} alt="cover" className="cover-image" />
        <div className="profile-container">
          <img src={imageSrc} alt="profile" className="profile-image" />
          <div className="employee-info">
            <h2>{employeeName}</h2>
            <p>{displayRole}</p>
          </div>
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === "ProfileDetails" ? "active" : ""}
          onClick={() => setActiveTab("ProfileDetails")}
        >
          Personal Details
        </button>
        <button
          className={activeTab === "EducationDetails" ? "active" : ""}
          onClick={() => setActiveTab("EducationDetails")}
        >
          Qualification
        </button>
        <button
          className={activeTab === "SocialMedia" ? "active" : ""}
          onClick={() => setActiveTab("SocialMedia")}
        >
          Social Media
        </button>
        <button
          className={activeTab === "CertificationDetails" ? "active" : ""}
          onClick={() => setActiveTab("CertificationDetails")}
        >
          Certification Details
        </button>
      </div>

      <div className="tab-content">
        {activeTab === "ProfileDetails" && (
          <PersonalDetails profileData={profileData} />
        )}
        {activeTab === "EducationDetails" && (
          <QualificationDetails profileData={profileData} />
        )}
        {activeTab === "SocialMedia" && (
          <SocialMediaLinks profileData={profileData} />
        )}
        {activeTab === "CertificationDetails" && (
          <CertificationHistory profileData={profileData} />
        )}
      </div>
    </div>
  );
}

export default AccountSettings;
